//frontend/src/components/visualiser/TrackLane.jsx

import React, { useMemo } from 'react';
import StopMarker from './StopMarker';
import BusWidget from './BusWidget';
import { formatIsoTime } from '@core/domain/time';
import {
  orderStopsForLane,
  buildStopIndexMap,
} from '@core/adapters/LondonVisualiserStops';
import {
  buildVehicleExpectedTimes,
  sortBusesWithSelectedLast,
  groupBusesByStop,
  buildBusRankMap,
} from '@core/adapters/LondonVisualiserBuses';
import { computeBusLayout } from '@core/adapters/LondonVisualiserLayout';

export default function TrackLane({
  stops,
  buses,
  trackColor,
  label,
  busAbove,
  onStopClick,
  direction,
  avgHeadway,
  expectedArrivals,
  selectedVehicleId,
  onSelectVehicle,
}) {
  const stopList = Array.isArray(stops) ? stops : [];

  const orderedStops = useMemo(
    () => orderStopsForLane(stopList, direction),
    [stopList, direction]
  );

  const stopIndexMap = useMemo(
    () => buildStopIndexMap(orderedStops, direction),
    [orderedStops, direction]
  );

  const selectedVehicleExpectedTimes = useMemo(
    () => buildVehicleExpectedTimes(expectedArrivals, selectedVehicleId, direction),
    [expectedArrivals, selectedVehicleId, direction]
  );

  const sortedBuses = useMemo(
    () => sortBusesWithSelectedLast(buses, selectedVehicleId),
    [buses, selectedVehicleId]
  );

  const busGroups = useMemo(
    () => groupBusesByStop(sortedBuses, stopIndexMap),
    [sortedBuses, stopIndexMap]
  );

  const busRankMap = useMemo(
    () => buildBusRankMap(busGroups),
    [busGroups]
  );

  const selectedBus = buses.find(b => b.vehicle_id === selectedVehicleId);

  return (
    <div style={{ marginBottom: '2rem' }}>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase',
        color: trackColor, opacity: 0.85, marginBottom: 8,
      }}>{label}</div>

      <div style={{ position: 'relative', height: 'var(--visualiser-track-height)' }}>
        {/* Track line */}
        <div style={{
          position: 'absolute', left: 0, right: 0, top: '50%', height: 5,
          background: trackColor, borderRadius: 3, transform: 'translateY(-50%)',
        }} />

        {/* Stop markers */}
        {orderedStops.map((stop, i) => {
          const pct = orderedStops.length === 1 ? 50 : (i / (orderedStops.length - 1)) * 100;
          return (
            <div
              key={`${stop.id}-${stop.type}-${stop.sequence_no}-${i}`}
              style={{ position: 'absolute', left: `${pct}%`, top: '50%', transform: 'translate(-50%, -50%)' }}
            >
              <StopMarker
                stop={stop}
                trackColor={trackColor}
                tipBelow={busAbove}
                onClick={() => onStopClick({ ...stop, direction, trackColor })}
              />
            </div>
          );
        })}

        {/* Bus widgets */}
        {sortedBuses.map(bus => {
          const busNaptan = String(bus.naptan_id).trim().toUpperCase();
          const matchedIndex = stopIndexMap.get(busNaptan);
          const rank = busRankMap.get(bus.vehicle_id);
          const isSelected = bus.vehicle_id === selectedVehicleId;

          const { positionPercent, top, zIndex } = computeBusLayout(bus, {
            matchedIndex,
            totalStops: orderedStops.length,
            busAbove,
            rank,
            isSelected,
          });

          return (
            <div
              key={bus.vehicle_id}
              style={{
                position: 'absolute',
                left: `${positionPercent}%`,
                top,
                transform: busAbove ? 'translate(-50%, -100%)' : 'translateX(-50%)',
                zIndex,
                pointerEvents: 'auto',
                transition: 'left 1.5s ease-out',
                willChange: 'left',
              }}
            >
              <BusWidget
                bus={bus}
                above={busAbove}
                avgHeadway={avgHeadway}
                isSelected={isSelected}
                onSelect={() => onSelectVehicle(bus.vehicle_id)}
              />
            </div>
          );
        })}

        {/* Expected arrival labels for the selected vehicle */}
        {selectedVehicleExpectedTimes && selectedVehicleExpectedTimes.size > 0 && (
          <>
            {orderedStops.map((stop, i) => {
              if (stop.type !== 1) return null;
              const pct = orderedStops.length === 1 ? 50 : (i / (orderedStops.length - 1)) * 100;
              const isCurrentStop = selectedBus && stop.naptan_id === selectedBus.naptan_id;

              let timeToShow;
              if (isCurrentStop && selectedBus?.expected_arrival) {
                timeToShow = formatIsoTime(selectedBus.expected_arrival);
              } else {
                const expectedTime = selectedVehicleExpectedTimes.get(stop.naptan_id);
                timeToShow = expectedTime ? formatIsoTime(expectedTime) : null;
              }

              if (!timeToShow || timeToShow === '-') return null;

              const verticalOffset = 18;
              const topPos = busAbove
                ? `calc(50% + ${verticalOffset}px)`
                : `calc(50% - ${verticalOffset}px)`;

              return (
                <div
                  key={`expected-${i}`}
                  style={{
                    position: 'absolute',
                    left: `${pct}%`,
                    top: topPos,
                    transform: busAbove
                      ? 'translate(-50%, 0) rotate(-30deg)'
                      : 'translate(-50%, -100%) rotate(-30deg)',
                    pointerEvents: 'none',
                    zIndex: 100 - i,
                    fontFamily: 'var(--font-mono)',
                    fontSize: 9,
                    fontWeight: isCurrentStop ? 1000 : 400,
                    color: 'var(--text-secondary)',
                    background: 'var(--bg-surface)',
                    padding: '1px 4px',
                    borderRadius: 4,
                    border: isCurrentStop ? '1px solid var(--accent)' : '1px solid var(--border-subtle)',
                    boxShadow: 'var(--shadow-sm)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {timeToShow}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}